import * as ko from "knockout";
import * as pli from "pli/forms";
import { EditGrid } from "pli/ko-editgrid/editGrid";
import { ShortcutMap } from "pli/util/keymap";
import { GenerateGuid, buildQualifiedZone } from "./utils";
import { LandItem, RightItem, CommentItem, TractItem, TractItemRecord, LandItemRecord, RightItemRecord, CommentItemRecord } from "./landsRightsViewModel";

/* tslint:disable */
const selectionForm = require("../common-selectionform");
/* tslint:enable */

const columns = [
    { id: "description", field: "description", width: 569, title: "Description" },
    { id: "tractNumber", field: "tractNumber", width: 60, title: "Group" },
    { id: "hasNotes", field: "hasNotes", width: 60, title: "Notes", formatter: "hasNotes:bool-icon", formatterTrueIconClass: "sprite-note-16", cssClass: "center" }
];


/**
 * Returns true if a row has errors.
 *
 * TODO: Remove once https://github.com/pandell/web-pli/issues/394 has been addressed.
 */
function rowHasErrors(row: any): boolean {
    const errors = row._errors;
    if (typeof errors !== "object") { return false; };
    for (const k in errors) {
        if (errors.hasOwnProperty(k) && errors[k] !== undefined) {
            return true;
        }
    }
    return false;
}

/**
 * Computes the description to be display on the edit grid
 * based on the different tract item types.
 *
 * Example.
 * Land: "TWP 16-RGE 22-W4M S 21"
 *
 * Right: "PNG from SURFACE to BASEMENT"
 *
 * Comment: "This is a comment for the tract"
 *
 * @param record
 *     The tract record to compute the description string.
 */
export function computeDescription(record: TractItemRecord): string {
    switch (record.type) {
        case "land":
            const landItem = <LandItemRecord>record;
            const location = landItem.location || "";
            const sections = landItem.sections;
            if (landItem.sections && landItem.sections.length > 0) {
                return "".concat(location).concat(" ").concat(sections);
            }

            return location || "(Empty)";
        case "right":
            const rightItem = <RightItemRecord> record;
            const from = buildQualifiedZone(rightItem.from, rightItem.fromZone);
            const to = buildQualifiedZone(rightItem.to, rightItem.toZone);
            let result = (rightItem.excluding ? "Excl. " : "");
            result = result + (rightItem.product || "");
            if (from) {
                result = result + " from " + from;
            }
            if (to) {
                result = result + " to " + to;
            }

            return result || "(Empty)";
        case "comment":
            const commentItem = <CommentItemRecord> record;

            return commentItem.comment || "(Empty)";
        default:
            return "";
    }
};

/**
 * Computes whether the tract item record has notes
 * entered or not.
 *
 * @param notes
 *     String representing the note value.
 */
function computeHasNotes(notes: string): boolean {
    return !!notes && notes !== "";
};

/**
 * Computes the tract item description and notes columns
 *
 * @param tractItemRecords
 *     The tractItemRecords from the grid that need to have
 *     their columns computed.
 */
function computeColumns(tractItemRecords: TractItemRecord[]): void {
    for (const item of tractItemRecords) {
        item.description = computeDescription(item);
        item.hasNotes = computeHasNotes(item.notes);
    }
}

/**
 * Determines the max group number in the array of
 * tract item records.
 *
 * @param tractItems
 *     Array of tract item records.
 */
function findMaxGroupNumber(tractItems: TractItemRecord[]): number {
    let l: number, i: number, n: number, max = 0;
    if (tractItems) {
        for (i = 0, l = tractItems.length; i < l; i += 1) {
            n = tractItems[i].tractNumber;
            if (n > max) { max = n; }
        }
    }
    return max;
}

/**
 * Determines the max group number per tract item type.
 *
 * @param tractItems
 *     Array of tract item records.
 *
 * @param type
 *     The type of tract item records to find the max
 *     group number.
 */
function findMaxGroupNumberPerType(tractItems: TractItemRecord[], type: string): number {
    let max = 0;

    let items = tractItems.filter( (item: TractItemRecord): boolean => {
            return item.type === type;
        });

    max = findMaxGroupNumber(items);

    return max + 1;
}

/**
 * Renumbers the tract number when copying tract items
 * from a different document.
 *
 * @param tracts
 *     Array of tracts to be renumbered.
 *
 * @param start
 *     Starting tract number from existing tracts.
 */
function renumberTractNum(tracts: any[], start: number): any {
    let l: number, i: number;
    if (tracts) {
        for (i = 0, l = tracts.length; i < l; i += 1) {
            tracts[i].id = start + i + 1;
            tracts[i].name = start + i + 1;
        }
    }
    return tracts;
}

/**
 * Edit grid view model used for lands and rights tab.
 */
export class LandsRightsEditGridViewModel {
    /**
     * Create a new LandsRightsEditGridViewModel instance.
     *
     * @param items
     *     Observable field outside of the LandsRightsEditGridViewModel
     *     to detect changes within the edit grid in order
     *     to correctly set the global dirty flag.
     */
    constructor(items: ko.Observable<any>) {
        this._pendingErrors = {};
        this._tracts = items || ko.observableArray();
        this._tractIds = [];

        const onItemsCommitted = (committedItems: TractItemRecord[]) => {
            computeColumns(committedItems);
            this._buildTracts(this.editGrid.items());
        };

        this.editGrid = new EditGrid({
            columns: columns,
            // TODO: remove custom rowCssProvider once https://github.com/pandell/web-pli/issues/394 has been addressed
            rowCssProvider: row => rowHasErrors(row)
                ? ["error"]
                : (!!row.isNewRecord ? ["new"] : []),
            id: "landsRightsEditGrid",
            idField: "rowId",
            shortcutActions: {},
            createViewItem: (records: TractItemRecord[]): TractItem => {
                if (records.length > 0) {
                    return records[0].type === "land"
                        ? new LandItem(records[0])
                        : records[0].type === "right"
                            ? new RightItem(records[0])
                            : new CommentItem(records[0], this.editGrid);
                }
                return new LandItem();
            },
            onItemsCommitted: onItemsCommitted,
            multiSelect: false
        });
    }


    // -------------------------------------------------------------------------
    // Public methods
    // -------------------------------------------------------------------------

    /**
     * Adds a new tract item record to the edit grid.
     *
     * @param type
     *     The type of tract item to be added.
     *
     *     Available types: Land, right and comment.
     */
    addItem(type: string): boolean {
        const uniqueId = GenerateGuid();

        // if it is a new land, default the format to DLS
        if (type === "land") {
            this.editGrid.addItem(<TractItemRecord>{ rowId: uniqueId, type: type, format: "DLS", tractNumber: findMaxGroupNumberPerType(this.editGrid.items(), type), isNewRecord: true });
        } else {
            this.editGrid.addItem({ rowId: uniqueId, type: type, tractNumber: findMaxGroupNumberPerType(this.editGrid.items(), type), isNewRecord: true });
        }

        // make sure the observable is up to date
        this._buildTracts(this.editGrid.items());
        this.editGrid.apply({ resize: true });

        return false;
    }

    /**
     * Copy tract items from another document
     * using the selection from.
     */
    copyItems(): void {
        /**
         * Show selection form that allows users
         * to find and copy tracts to this array.
         *
         * @remarks
         *     Expects "this" to be a grid observable
         *     array that creates and stores tracts.
         */
        selectionForm.open({
            getDetailsApi: "Geo-Tract-GetDetails",
            getDetailsDataBuilder: function (items: any): any {
                let guidIndex = items[0].length - 1;

                return {
                    removeRights: (pli.viewModel.extras.title().indexOf("Surface Lease") > -1), // Remove rights from tract if surface lease
                    copyTractFrom: ko.utils.arrayMap(items, function (item: any): any {
                        return {
                            guid: item[guidIndex],
                            type: item[3]
                        };
                    })
                };
            },
            multiSelect: true,
            searchApi: "Geo-Document-SearchTracts",
            text: {
                acceptButton: "Copy",
                iconClass: "sprite-copy-16",
                examples: [
                    "<strong>File Numbers</strong>:<br>C-12 will find C-123, C-1234, etc.<br>M001 will find M001, M0019, etc.",
                    "<strong>Areas</strong>:<br>river will find GHOST RIVER, TWO RIVERS, etc.",
                    "<strong>Lands</strong>:<br>68 17 4 will find Twp 68-Rge 17-W4M<br>94 h 13 c will find 94-H-11-C<br>Note: Sections are not searched",
                    "<strong>Combinations</strong>:<br>C, lloyd will find contracts (starting with C) in the Lloydminster area"
                ],
                title: "Copy Tracts"
            },
            onAcceptedContext: this,
            onAccepted: function (newTracts: any): any {
                newTracts = selectionForm.utils.resetGuids(newTracts);

                let records = renumberTractNum(newTracts, findMaxGroupNumber(this.editGrid.items.peek()));
                let newTractItems = this._flattenTracts(records);

                // these newly copied tractItems don't have rowIds yet, so give them one
                for (let tractItem of newTractItems) {
                    tractItem.rowId = GenerateGuid();
                }

                // add the copied tractItems to the existing ones
                newTractItems = this.editGrid.items().concat(newTractItems);
                computeColumns(newTractItems);

                this.editGrid.apply({ items: newTractItems });

                // make sure the observable is up to date
                this._buildTracts(this.editGrid.items());
            }
        });
    }

    /**
     * Removes a tract item record from the edit grid.
     */
    deleteItem(): boolean {
        const itemToRemove = this.editGrid.currentItems();

        if (itemToRemove.length !== 0) {
            this.editGrid.deleteItems(itemToRemove);
        }

        // make sure the observable is up to date
        this._buildTracts(this.editGrid.items());

        return false;
    }

    /**
     * Returns the error count of the items in the
     * edit grid.
     *
     * TODO: set back to this.editGrid.errorsCount() once https://github.com/pandell/web-pli/issues/394 has been addressed
     */
    errorsCount(): number {
        return this.editGrid.items().filter(rowHasErrors).length;
    }


    /**
     * Clear out _tractIds. This is useful when duplicating a file
     */
    resetTractIds(): void {
        this._tractIds = [];
    }

    /**
     * Load an array of tract ids which is a flattened mapping
     * of the tract guid and tract item guid.
     *
     * This mapping array is required to build the tract
     * items from the edit grid back to the tracts.
     */
    loadViewModel(tracts: any): void {
        /**
         * Fixes EditGrid bug https://github.com/pandell/web-pli/issues/625
         * TODO: Remove when fixed in web-pli
         */
        const focusedItem = this.editGrid.focusedItem();
        this.editGrid.apply({ focusedItem: null });

        this._tractIds = [];
        if (tracts) {
            tracts.forEach(function (tract: any): void {
                if (tract.tractItems && tract.tractItems.length > 0) {
                    tract.tractItems.forEach(function (tractItem: any): void {
                        this._tractIds.push({ tractGuid: tract.guid, tractNum: tract.name, tractItemGuid: tractItem.isRight ? tractItem.right.guid : tractItem.land.guid });
                    }, this);
                } else {
                    this._tractIds.push({ tractGuid: tract.guid, tractNum: tract.name, tractItemGuid: null });
                }
            }, this);
        }

        const flattenTracts = this._flattenTracts(tracts);
        computeColumns(flattenTracts);

        const shortcutActions: ShortcutMap<() => boolean> = {
            "ctrl+i": this.addItem.bind(this),
            "ctrl+delete": this.deleteItem.bind(this)
        };

        const onItemsCommitted = (committedItems: TractItemRecord[]) => {
            computeColumns(committedItems);
            this._buildTracts(this.editGrid.items());
        };

        this.editGrid.apply({
            columns: columns,
            idField: "rowId",
            multiSelect: false,
            shortcutActions: shortcutActions,
            onItemsCommitted: onItemsCommitted,
            items: flattenTracts,
            focusedItem: focusedItem
        });
    }


    // -------------------------------------------------------------------------
    // Private methods
    // -------------------------------------------------------------------------

    /**
     * Builds the tract items back into tracts.
     */
    private _buildTracts(tractItems: TractItemRecord[]): void {
        // if we set tracts to a simple array, we run into problems when
        // the user sets the tract number to 999999 because we then get
        // an array with 999999 elements that are mostly undefined.
        const tracts: { [index: string]: any } = {};
        const tractsDictionary: any = this._tractIds;

        ko.utils.arrayForEach(tractItems, function (item: TractItemRecord): void {
            if (item.tractNumber) {
                const num: number = item.tractNumber;
                const tractInfo = ko.utils.arrayFirst(tractsDictionary, function (id: any): boolean {
                    return id.tractNum === item.tractNumber;
                });

                tracts[num] = {
                    id: num,
                    guid: tractInfo ? tractInfo.tractGuid : "",
                    number: num,
                    name: num,
                    description: null,
                    tractItems: []
                };
            }
        });

        ko.utils.arrayForEach(tractItems, function (item: TractItemRecord, index: number): void {
            const tractItem: any = { land: {}, right: {} };
            const num: number = item.tractNumber;
            const guid: string = item.guid;
            const tractInfo = ko.utils.arrayFirst(tractsDictionary, function (id: any): boolean {
                return id.tractNum === num && id.tractItemGuid === guid;
            });

            // if the user removes the group number for some reason, we don't want to throw an error
            if (num) {
                if (item.type === "land") {
                    const landItem = <LandItemRecord>item;
                    tractItem.land.id = landItem.description;
                    tractItem.land.guid = tractInfo ? landItem.guid : "";
                    tractItem.land.format = landItem.format;
                    tractItem.land.location = landItem.location;
                    tractItem.land.sections = landItem.sections;
                    tractItem.land.lease = landItem.lease;
                    tractItem.land.lessor = landItem.lessor;
                    tractItem.land.productionType = landItem.productionType;
                    tractItem.land.description = landItem.notes;
                    tractItem.isRight = false;
                    tractItem.originalIndex = index;

                    tracts[num].tractItems.push(tractItem);

                } else if (item.type === "right") {
                    const rightItem = <RightItemRecord>item;
                    tractItem.right.id = rightItem.description;
                    tractItem.right.guid = tractInfo ? rightItem.guid : "";
                    tractItem.right.excluding = rightItem.excluding;
                    tractItem.right.product = rightItem.product;
                    tractItem.right.from = rightItem.from;
                    tractItem.right.fromZone = rightItem.fromZone;
                    tractItem.right.to = rightItem.to;
                    tractItem.right.toZone = rightItem.toZone;
                    tractItem.right.description = rightItem.notes;
                    tractItem.isRight = true;
                    tractItem.originalIndex = index;

                    tracts[num].tractItems.push(tractItem);

                } else if (item.type === "comment") {
                    const commentItem = <CommentItemRecord>item;
                    tracts[num].description = commentItem.comment;
                }
            }
        });

        // convert the tracts dictionary back into an array so we can give it to the observable
        let tempTracts: any[] = [];
        for (let key in tracts) {
            if (tracts.hasOwnProperty(key)) {
                tempTracts.push(tracts[key]);
            }
        }
        this._tracts(tempTracts);
    }

    /**
     * Flattens the tracts into the tract items.
     */
    private _flattenTracts(tracts: any[]): TractItemRecord[] {
        let tractItems: any[] = [];

        if (tracts) {
            // flatten the data so it displays properly in the grid
            for (let tract of tracts) {
                for (let tractItem of tract.tractItems) {
                    if (tractItem.isRight) {
                        // if the items are coming from a copied split, all the guids have been cleared.
                        const guid = tractItem.right.guid === null ? GenerateGuid() : tractItem.right.guid;
                        tractItems.push({
                            guid: guid,
                            rowId: guid,
                            tractNumber: tract.name,
                            type: "right",
                            notes: tractItem.right.description,
                            excluding: tractItem.right.excluding,
                            product: tractItem.right.product,
                            from: tractItem.right.from,
                            fromZone: tractItem.right.fromZone,
                            to: tractItem.right.to,
                            toZone: tractItem.right.toZone
                        });
                    } else {
                        // if the items are coming from a copied split, all the guids have been cleared.
                        const guid = tractItem.land.guid === null ? GenerateGuid() : tractItem.land.guid;
                        tractItems.push({
                            guid: guid,
                            rowId: guid,
                            tractNumber: tract.name,
                            type: "land",
                            notes: tractItem.land.description,
                            format: tractItem.land.format,
                            location: tractItem.land.location,
                            sections: tractItem.land.sections,
                            lease: tractItem.land.lease,
                            lessor: tractItem.land.lessor,
                            productionType: tractItem.land.productionType
                        });
                    }
                }

                if (tract.description && tract.description !== "") {
                    // if the items are coming from a copied split, all the guids have been cleared.
                    const guid = tract.guid === null ? GenerateGuid() : tract.guid;
                    tractItems.push({
                        guid: guid,
                        rowId: guid,
                        tractNumber: tract.name,
                        type: "comment",
                        comment: tract.description
                    });
                }
            }
        }

        return tractItems;
    }


    // -------------------------------------------------------------------------
    // Public properties
    // -------------------------------------------------------------------------

    /** Represents the edit grid component */
    editGrid: EditGrid<TractItem, TractItemRecord>;


    // -------------------------------------------------------------------------
    // Private properties
    // -------------------------------------------------------------------------

    private _pendingErrors: { [index: number]: { [key: string]: string } };

    /** Stores the tract representation of the tract items in the edit grid. */
    private _tracts: ko.Observable<any[]>;

    /** Mapping of tract ids to tract item ids. */
    private _tractIds: any[];
}

module.exports = LandsRightsEditGridViewModel;
module.exports.computeDescription = computeDescription;



// WEBPACK FOOTER //
// ./app/document-landright-ts/landsRightsEditGridViewModel.ts